import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import LocationSelect from './LocationSelect';
import { LocationOption, ProductId } from '../types';
import { getProductById } from '@shared/products';
import { PurchaseValidation, UserInfoFormData, PurchaseData } from '@shared/purchase';
import Logo from './ui/Logo.tsx';
import CTA from './ui/CTA.tsx';
import ProductImage from './ui/ProductImage.tsx';
import CTAFooter from './ui/CTAFooter.tsx';

interface EmailValidationProps {
  purchaseId: string;
}

const EmailValidation: React.FC<EmailValidationProps> = ({ purchaseId }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [step, setStep] = useState<'validation' | 'form' | 'download' | 'confirmation'>('validation');
  const [purchaseData, setPurchaseData] = useState<PurchaseData | null>(null);
  const [processingDownload, setProcessingDownload] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<UserInfoFormData>();

  const [selectedProductId, setSelectedProductId] = useState<ProductId | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<LocationOption | null>(null);

  useEffect(() => {
    // Set customer email if we have it from validation
    if (purchaseData?.customerEmail) {
      setValue('email', purchaseData.customerEmail);
    }
  }, [purchaseData, setValue]);

  const handleEmailValidation = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setError('Por favor, insira seu endereço de email');
      return;
    }

    if (!purchaseId) {
      setError('ID de compra inválido');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Call Firebase function to validate purchase and email
      const validatePurchase = httpsCallable(functions, 'getUserInfo');
      const result = await validatePurchase({
        purchaseId,
        customerEmail: email.trim()
      });

      const validation: PurchaseValidation = result.data as PurchaseValidation;

      if (!validation.isValid) {
        setError('ID de compra ou endereço de email inválido. Por favor, verifique suas informações e tente novamente.');
        return;
      }

      if (validation.status === "WAITING") {
        setStep('confirmation');
        return;
      }

      // Create purchase data object
      const data: PurchaseData = {
        purchaseId: purchaseId,
        customerEmail: validation.customerEmail || email.trim(),
        pdfGenerated: validation.hasMap,
        pdfUrl: validation.pdfUrl,
        userInfoSubmitted: !validation.hasMap,
        productId: validation.purchaseData?.productId || 'astral-map'
      };

      setPurchaseData(data);
      setSelectedProductId(data.productId);

      // If purchase is valid and has a map, show download
      if (validation.hasMap && validation.pdfUrl) {
        setStep('download');
      } else {
        // If purchase is valid but no map, show form
        setStep('form');
      }

    } catch (error: any) {
      console.error('Error validating purchase:', error);
      setError(error.message || 'Falha ao validar a compra. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmission = async (data: UserInfoFormData) => {
    if (!purchaseId) {
      setError('ID de compra é obrigatório');
      return;
    }

    if (!selectedLocation) {
      setError('Por favor, selecione seu local de nascimento');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const submitUserInfo = httpsCallable(functions, 'submitUserInfo');

      // Add latitude and longitude from selected location
      const submissionData = {
        ...data,
        latitude: selectedLocation?.lat || 0,
        longitude: selectedLocation?.lon || 0
      };

      await submitUserInfo({
        purchaseId,
        ...submissionData
      });

      setSuccess('Suas informações foram enviadas com sucesso! Seu mapa astral será gerado em até 24 horas. Você receberá uma notificação por email quando estiver pronto para download.');

      // Show confirmation step instead of redirecting
      setStep('confirmation');

    } catch (error: any) {
      console.error('Error submitting user info:', error);
      setError(error.message || 'Falha ao enviar informações. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!purchaseData?.pdfUrl) {
      setError('Link de download não disponível');
      return;
    }

    setProcessingDownload(true);

    try {
      // Call Firebase Function to get fresh download URL
      const downloadPDF = httpsCallable(functions, 'downloadPDF');
      const result = await downloadPDF({ purchaseId });

      if (result.data && (result.data as any).pdfUrl) {
        // Open PDF in new tab
        window.open((result.data as any).pdfUrl, '_blank');
      } else {
        setError('Falha ao obter link de download');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setError('Falha ao baixar PDF. Por favor, tente novamente.');
    } finally {
      setProcessingDownload(false);
    }
  };

  // Email Validation Step
  if (step === 'validation') {
    return (
      <div className="container max-w-3xl bg-white mx-auto rounded-2xl">
        <div className="card">
          <div className="header p-8 pb-0">
            <Logo />
            <p className='mt-8'>Por favor, insira o endereço de email que você usou ao comprar seu mapa astral</p>
          </div>

          {error && <div className="error mt-5">{error}</div>}
          {success && <div className="success mt-5">{success}</div>}

          <form onSubmit={handleEmailValidation} className='m-4 md:m-8 bg-gray-100 border border-gray-200 rounded-lg p-4 md:p-6 shadow-md'>
            <div className="form-group">
              <label htmlFor="email">Endereço de Email *</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Digite seu endereço de email"
                required
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary rounded-full"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="loading"></span>
                  Validando...
                </>
              ) : (
                'Validar Compra'
              )}
            </button>
          </form>


          <CTA />
        </div>
      </div>
    );
  }

  // Form Step
  if (step === 'form') {
    return (
      <div className="container max-w-3xl bg-white mx-auto rounded-2xl pb-8">
        <div>
          <div className="header flex flex-col gap-4">
            <div className="bg-gray-100 rounded-2xl">
              <div className='h-48 md:h-72 p-6 flex relative'>
                <ProductImage productId={selectedProductId} />
              </div>
            </div>
            <h1 className='text-2xl md:text-3xl font-bold'>{selectedProductId && getProductById(selectedProductId).displayName}</h1>
            <p className='text-sm md:text-base px-4 md:px-0'>Por favor, forneça seus dados de nascimento para gerar seu {selectedProductId && getProductById(selectedProductId).displayName.toLowerCase()} personalizado</p>
          </div>

          {/* {selectedProductId && (
            <ProductInfo productId={selectedProductId} />
          )} */}


          {error && <div className="error mt-5">{error}</div>}
          {success && <div className="success mt-5">{success}</div>}

          {/* <button 
            className="btn btn-secondary" 
            onClick={handleBackToValidation}
            style={{ marginBottom: '20px' }}
          >
            ← Voltar para Validação
          </button> */}

          <form onSubmit={handleSubmit(handleFormSubmission)} className='m-2 md:m-8 bg-gray-100 border border-gray-200 rounded-lg p-4 md:p-6 shadow-md'>

            <div className="form-group">
              <label htmlFor="email">Endereço de Email *</label>
              <input
                id="email"
                type="email"
                {...register('email', {
                  required: 'Email é obrigatório',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Endereço de email inválido'
                  }
                })}
                placeholder="seu.email@exemplo.com"
                disabled={true}
              />
              {errors.email && <span className="error">{errors.email.message}</span>}
            </div>


            <div className="form-group">
              <label htmlFor="fullName">Nome Completo *</label>
              <input
                id="fullName"
                type="text"
                {...register('fullName', { required: 'Nome completo é obrigatório' })}
                placeholder="Digite seu nome completo"
              />
              {errors.fullName && <span className="error">{errors.fullName.message}</span>}
            </div>

            <div className='grid grid-cols-2 gap-4'>
              <div className="form-group">
                <label htmlFor="birthDate">Data de Nascimento *</label>
                <input
                  id="birthDate"
                  type="date"
                  {...register('birthDate', { required: 'Data de nascimento é obrigatória' })}
                />
                {errors.birthDate && <span className="error">{errors.birthDate.message}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="birthTime">Horário de Nascimento *</label>
                <input
                  id="birthTime"
                  type="time"
                  {...register('birthTime', { required: 'Horário de nascimento é obrigatório' })}
                />
                {errors.birthTime && <span className="error">{errors.birthTime.message}</span>}
              </div>
            </div>

            <input
              type="hidden"
              {...register('birthPlace', { required: 'Local de nascimento é obrigatório' })}
            />

            <div className="form-group">
              <label htmlFor="birthPlace">Local de Nascimento *</label>
              <LocationSelect
                value={selectedLocation}
                onChange={(location) => {
                  setSelectedLocation(location);
                  if (location) {
                    setValue('birthPlace', location.label);
                  }
                }}
                placeholder="Pesquise seu local de nascimento..."
                isInvalid={!selectedLocation}
                errorMessage={!selectedLocation ? 'Por favor, selecione seu local de nascimento' : undefined}
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="loading"></span>
                  Enviando Suas Informações...
                </>
              ) : (
                'Enviar Informações'
              )}
            </button>
          </form>

          <CTAFooter />

        </div>
      </div>
    );
  }

  // Download Step
  if (step === 'download' && purchaseData) {
    return (
      <div className="container max-w-4xl bg-white mx-auto rounded-3xl shadow-2xl overflow-hidden">
        {/* Success Header */}
        <div className='flex justify-center p-8'>
          <Logo />
        </div>
        
        <div className="p-4 text-center">
          <div>
            
            <div className="flex justify-center mb-3">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center shadow-lg">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            Seu mapa está pronto!
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Seu mapa personalizado foi gerado com base em suas informações de nascimento
          </p>
        </div>

        <div className="p-8">
          {purchaseData.pdfGenerated && purchaseData.pdfUrl && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-8 mb-8 border border-green-200">
             

              <div className="flex justify-center">
                <button
                  className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-bold text-lg rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                  onClick={handleDownload}
                  disabled={processingDownload}
                >
                  <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  {processingDownload ? (
                    <>
                      <span className="loading"></span>
                      Preparando Download...
                    </>
                  ) : (
                    `Baixar ${purchaseData && getProductById(purchaseData.productId).displayName || 'Mapa Astral'} PDF`
                  )}
                </button>
              </div>

              <div className="mt-6 text-center">
                <p className="text-sm text-gray-500">
                  Não consegue baixar? Verifique seu email em{' '}
                  <span className="font-semibold text-gray-700">{purchaseData.customerEmail}</span>
                </p>
              </div>
            </div>
          )}

          <CTAFooter />
        </div>
      </div>
    );
  }

  // Confirmation Step
  if (step === 'confirmation') {
    return (
      <div className="container max-w-4xl bg-white mx-auto rounded-3xl shadow-2xl overflow-hidden">
        {/* Success Header */}
        <div className='flex justify-center p-8'>
          <Logo />
        </div>
        
        <div className="p-8">
          {/* Main Success Message */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-6">
              <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
              Vamos criar o seu mapa astral
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Recebemos suas informações e nossos astrólogos estão agora criando seu mapa astral.
            </p>
          </div>

          {/* Progress Steps */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 mb-8">
            <h3 className="text-xl font-bold text-gray-800 mb-6 text-center">
              O que acontece agora?
            </h3>
            <div className="grid md:grid-cols-2 gap-6 text-left">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800">Dados Registrados</h4>
                  <p className="text-gray-600 text-sm">Seus dados de nascimento foram registrados com sucesso</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800">Criação</h4>
                  <p className="text-gray-600 text-sm">Seu mapa astral ficará pronto em até 24 horas</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800">Notificação</h4>
                  <p className="text-gray-600 text-sm">Você receberá uma notificação por email quando estiver pronto</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800">Download</h4>
                  <p className="text-gray-600 text-sm">Você pode baixar seu mapa pelo email ou retornar aqui</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <CTAFooter />
        </div>
      </div>
    );
  }

  return null;
};

export default EmailValidation; 